// THE SHELL. Every screen is a route with a URL, because browser back and forward and a shared link all
// have to work — that is the prototype's design and it is kept (§4 "Better" #8).
//
// `/` IS THE ENTRY AGAIN SINCE WEB-PAY-2 (2026-09-29). W5 made it this app's own free check (`v2FreeCheck`); that door
// was retired in favour of main's website door (R151), so there is ONE free check. `/free-check` hands off to it, and
// `/` takes back what it hands in — an address and a unit on the URL — as text for the boxes and nothing more
// (`app/handIn.ts`). Nothing past `/` changed: the gates, the checkout and every `PaidRoute` step are as they were.
//
// It is also the half of that design that went wrong: every screen having a URL is only safe when every
// screen asks the server what it may show. That is `PaidRoute`, and it wraps every step — the app's seven since
// WEB-PARITY-5, and the generation overlay over the sixth.
//
// WHAT EACH SCREEN NEEDS BEFORE IT CAN ASK (WEB-APPEAL-ID). The checkout's spend, the steps and the outcome name
// an appeal, so they render under `RequireAppeal`; the three gates re-send the typed address, so they render under
// `NeedsTypedAddress`. Without what it needs, a screen sends the owner to `/` rather than a call with nothing in it.

import { Route, Routes, useLocation } from "react-router-dom";
import type { AppealSnapshot } from "@contract";
import { AccountsRoute, Busy, signedIn, useAuthWatch, useSignInEnded, WebAccountsProvider, type WebAccounts } from "./app/accounts";
import { useCall } from "./app/useCall";
import { RequireAppeal, useSession } from "./app/session";
import { AppBar, Focus, Work } from "./components/layout";
import { Entry, FreeCheckHandoff } from "./routes/Entry";
import { FREE_CHECK_URL } from "./app/handIn";
import { NeedsTypedAddress, NotAvailable, Property, Risk } from "./routes/gates";
import { Assent } from "./routes/assent";
import { CheckoutReturn } from "./routes/Checkout";
import { AccountAction } from "./routes/account/AccountAction";
import { AccountDoor, SignedOut } from "./routes/account/AccountDoor";
import { CheckoutGate } from "./routes/account/CheckoutGate";
import { Forgot } from "./routes/account/Forgot";
import { SignIn } from "./routes/account/SignIn";
import { Settings } from "./routes/account/Settings";
import { DeleteAccount } from "./routes/DeleteAccount";
import { Home, PropertyPage } from "./routes/home";
import { Outcome } from "./routes/outcomes";
import { PAID_ROUTES, type WorkspaceRoute } from "./routes/steps";
import { Analyze } from "./routes/steps/Analyze";
import { Comps } from "./routes/steps/Comps";
import { Condition } from "./routes/steps/Condition";
import { Details } from "./routes/steps/Details";
import { Letter } from "./routes/steps/Letter";
import { Verdict } from "./routes/steps/Verdict";
import { You } from "./routes/steps/You";

const STEP_PATHS = new Set<string>(PAID_ROUTES);

export function App() {
  const location = useLocation();
  const session = useSession();

  // ONE CALL ANSWERS THE CHROME. The address chip and whether the rail's links are live come from the same snapshot the
  // guards read — never from a local flag.
  const account = useCall(() => session.transport.v2Credits.get(), [session.transport, session.revision]);
  const appeal = account.status === "ok" ? account.response.appeal : null;
  const unlocked = appeal?.entitlement.unlocked ?? false;

  const inWorkspace = STEP_PATHS.has(location.pathname);

  // ONE ACCOUNT (ACC-3a): the server's WEB_ACCOUNTS_ENABLED, off the same call. Off — or unanswered — is today's site.
  const webAccounts: WebAccounts =
    account.status === "ok" ? account.response.body.capabilities.webAccounts : account.status === "pending" ? null : false;
  const accountsOn = webAccounts === true;
  useAuthWatch(accountsOn);
  // F3: a paid page after the sign-in ended under this page (deleted in the app, or revoked).
  const signInEnded = useSignInEnded(accountsOn);
  // WALK-FIX-1: a step, or the outcome, opened with no appeal held goes to the account's properties when there is an
  // account to list them for; otherwise to `/`, to start one, as before.
  // Until the switch is answered there is no telling which, so nothing is decided yet.
  const noAppeal = webAccounts === null ? null : accountsOn && signedIn(session.auth) ? "/home" : "/";

  return (
    <WebAccountsProvider value={webAccounts}>
      <a className="skip" href="#work">
        Skip to this step
      </a>
      <AppBar
        addressChip={session.typedAddress === "" ? undefined : session.typedAddress}
        appealStatus={appeal?.status}
        accountChip={accountsOn ? (signedIn(session.auth) ? "account" : "signIn") : "today"}
      />
      {inWorkspace && signInEnded ? (
        <Focus>
          <SignedOut />
        </Focus>
      ) : inWorkspace ? (
        <RequireAppeal otherwise={noAppeal} pending={<Busy />}>
          <WorkspaceRoutes current={location.pathname as WorkspaceRoute} unlocked={unlocked} appeal={appeal} />
        </RequireAppeal>
      ) : (
        <Focus wide={location.pathname === "/checkout"}>
          <Routes>
            <Route path="/" element={<Entry />} />
            <Route path="/free-check" element={<FreeCheckHandoff to={FREE_CHECK_URL} />} />
            <Route path="/not-available" element={<NeedsTypedAddress><NotAvailable /></NeedsTypedAddress>} />
            <Route path="/property" element={<NeedsTypedAddress><Property /></NeedsTypedAddress>} />
            <Route path="/risk" element={<NeedsTypedAddress><Risk /></NeedsTypedAddress>} />
            {/* ACC-3a: with the switch on, "Create your account" for a look-around; the assent page for an account, and
                for everyone with it off. `/signin`, `/forgot` and `/account/action` are the entry with it off, as any
                unknown route is today. `/checkout`'s gate is in `CheckoutGate`, which is today's checkout with it off. */}
            <Route path="/account" element={<AccountsRoute on={<AccountDoor />} off={<Assent />} pending={<Assent />} />} />
            <Route path="/signin" element={<AccountsRoute on={<SignIn />} off={<Entry />} />} />
            <Route path="/forgot" element={<AccountsRoute on={<Forgot />} off={<Entry />} />} />
            <Route path="/account/action" element={<AccountsRoute on={<AccountAction />} off={<Entry />} />} />
            {/* ACC-3b: the account's settings, with the switch on; the entry with it off, as any unknown route is. */}
            <Route path="/account/settings" element={<AccountsRoute on={<Settings />} off={<Entry />} />} />
            <Route path="/checkout" element={<RequireAppeal><CheckoutGate /></RequireAppeal>} />
            <Route path="/checkout/return" element={<CheckoutReturn />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/outcome" element={signInEnded ? <SignedOut /> : <RequireAppeal otherwise={noAppeal} pending={<Busy />}><Outcome /></RequireAppeal>} />
            <Route path="/home" element={<Home />} />
            <Route path="/home/:appealId" element={<PropertyPage />} />
            <Route path="*" element={<Entry />} />
          </Routes>
        </Focus>
      )}
    </WebAccountsProvider>
  );
}

/**
 * THE WORKSPACE'S ROUTES: the app's seven steps, and `/building`, the app's generation overlay over step 6
 * (WEB-PARITY-5). `/you` and `/building` are one screen — the Generate step — with the overlay closed or open, as the
 * app's `GenerateView` presents `LetterGenerationOverlay` over itself.
 *
 * NO LIVE PANEL (WEB-PARITY-5, ruling D4). The panel asked `compute`, `v2EvaluateCase` and the letter on every step to
 * fill a column the app does not have; the app's own cards now carry those facts on the steps that show them.
 */
function WorkspaceRoutes({ current, unlocked, appeal }: { current: WorkspaceRoute; unlocked: boolean; appeal: AppealSnapshot | null }) {
  return (
    <Work current={current} unlocked={unlocked} appeal={appeal}>
      <Routes>
        <Route path="/details" element={<Details />} />
        <Route path="/condition" element={<Condition />} />
        <Route path="/comps" element={<Comps />} />
        <Route path="/analyze" element={<Analyze />} />
        <Route path="/verdict" element={<Verdict />} />
        <Route path="/you" element={<You />} />
        <Route path="/building" element={<You />} />
        <Route path="/letter" element={<Letter />} />
      </Routes>
    </Work>
  );
}
